import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { circuulCampaignReportUrl } from '../../../configurations/api/url';
import type { RootState } from '../../../redux/root-reducer';
import type { CircuulReportSortBy, ICircuulCampaignReport } from '../../../types/global/circuulCampaignReport.types';

export function useAppInstallCampaignReport(campaignId: string | undefined) {
    const token = useSelector((s: RootState) => s.authentication.token);
    const [data, setData] = useState<ICircuulCampaignReport | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [sortBy, setSortBy] = useState<CircuulReportSortBy>('downloads');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
    const [geoSortBy, setGeoSortBy] = useState<'downloads' | 'clicks'>('downloads');
    const [search, setSearch] = useState('');

    const load = useCallback(async () => {
        if (!campaignId || !token) return;
        setLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams({
                sortBy,
                sortOrder,
                geoSortBy,
            });
            const res = await axios.get(`${circuulCampaignReportUrl(campaignId)}?${params}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setData(res.data?.data ?? null);
        } catch (err: any) {
            setData(null);
            setError(err?.response?.data?.message || err?.message || 'Unable to load app install report.');
        } finally {
            setLoading(false);
        }
    }, [campaignId, token, sortBy, sortOrder, geoSortBy]);

    useEffect(() => {
        void load();
    }, [load]);

    const creators = useMemo(() => {
        const rows = data?.creators ?? [];
        const q = search.trim().toLowerCase();
        if (!q) return rows;
        return rows.filter(r => r.name.toLowerCase().includes(q) || r.code.toLowerCase().includes(q));
    }, [data?.creators, search]);

    const maxGeoDownloads = useMemo(() => {
        const rows = data?.geo ?? [];
        return Math.max(1, ...rows.map(g => g.downloads || 0));
    }, [data?.geo]);

    return {
        data,
        loading,
        error,
        reload: load,
        sortBy,
        setSortBy,
        sortOrder,
        setSortOrder,
        geoSortBy,
        setGeoSortBy,
        search,
        setSearch,
        creators,
        maxGeoDownloads,
    };
}
